import { resourcesSubscriber } from '@/mqtt/resources'
import { getMessageSubscriber } from '@/store/modules/nextback/nextback.websocket.subscriber'
import SlideApi from '@/api/slide'
import SlideUtil from '@/utils/slide.utils'
import TimeUtil from '@/utils/time.utils'
import QuizStatuses from '@/constants/QuizStatuses'
import socket from '@/socket'
import { logger } from '@ahaslides-product/frontend-toolkit'

const logFn = logger.extend('nextback')

const NEXTBACK_SET_PRESENTING = 'nextback/setPresenting'
const onGoingRequestMap = {}

const _state = {
  slideMap: {},
  editor: null,
  slideList: [],
  activeSlideId: null,
  timestamp: null,
}

const getterMethods = {
  fetchSlideActionName: (_, __, ___, rootGetters) => {
    return rootGetters['featureToggles/canUseSlidePlugin'] ? 'fetchSlideDataAndAttributesAction' : 'fetchSlideAction'
  },
}

const actions = {
  subscribeSlideChangeAction: ({ commit, dispatch }, { presentation, editor }) => {
    if (presentation.audiencePacing) return
    if (!presentation.id) return
    commit('updateEditor', editor)
    dispatch('kvSync/listenConnectionEventAction', null, { root: true })
  },

  unsubscribeSlideChangeAction: (_, payload) => {
    resourcesSubscriber.unsubscribeResourceUri(`/LIVE_SESSION/${payload.id}/ACTIVE_ACTIVITY`)
  },

  changeSlideAction: async ({
    dispatch, state, commit, rootGetters,
  }, message) => {
    if (state.timestamp && message.timestamp && message.timestamp < state.timestamp) {
      logFn('changeSlideAction: timestamp is outdated', message)
      return
    }
    commit('setTimestamp', message.timestamp)

    // WHY: quizStatus is outdated if the quiz slide is played, then presenter esc and present again from the slide before the quiz, then navigate to that quiz slide.
    // the quizStatus is update in ahasync first, then in the DB -> we need to listen for the ahaSync to update the slideMap first.
    if (state.slideMap[message.id] && message.quizStatus) {
      commit('updateSlideMap', message)
    }
    let doc

    const isLeaderboardAction = ['leaderboard-invidual', 'leaderboard-team'].includes(message.actionFrom)
    if (state.activeSlideId == message.id && !isLeaderboardAction) {
      const activeSlide = rootGetters.activeActivity || state.slideMap[message.id]
      const isPluginQuiz = Boolean(activeSlide)
        && Boolean(rootGetters['embedApp/customSlideTypeByType'](activeSlide.type))
        && rootGetters.isQuizSlide(activeSlide)
      const isPluginQuizQuestion = message.quizStatus === QuizStatuses.QUESTION_SCREEN && isPluginQuiz
      if (message.quizStatus !== QuizStatuses.QUESTION_SCREEN || isPluginQuizQuestion) {
        doc = {
          _from: 'live',
          type: 'slide',
          action: 'update',
          data: message,
        }
      } else {
        doc = {
          _from: 'live',
          type: 'quiz-audience',
          action: 'startCountdownPickAnswer',
          data: {
            slideId: message.id,
            numberOfHintsShown: 0,
            quizStatus: message.quizStatus,
            quizTimestamp: message.quizTimestamp,
          },
        }
      }
    } else {
      const slide = await dispatch('getSlideDataAction', message.id)
      if (state.timestamp && message.timestamp && message.timestamp < state.timestamp) {
        logFn('changeSlideAction: timestamp is outdated after getSlideDataAction', message)
        return
      }
      const newSlide = { ...slide, ...message }
      commit('updateSlideMap', newSlide)
      doc = {
        _from: 'live',
        type: 'presentation',
        action: 'nextSlide',
        data: { ...message, id: message.id },
        socketId: message.socketId,
      }
    }

    const handler = getMessageSubscriber(doc.type)
    if (handler) {
      handler(doc, state.editor)
    }
    commit('setActiveSlideId', message.id)
    if (message.isNextSlide) {
      dispatch('prefetchNextSlideAction')
    }
  },

  prefetchSlidesAction: async ({ dispatch, commit, getters }, slideList) => {
    const delayInMs = 1000

    const fetchSlideWithDelay = async (slideId, isLast = false) => {
      const slide = await dispatch(getters.fetchSlideActionName, slideId)
      commit('updateSlideMap', slide)

      // Add delay after fetch, but not after the last slide
      if (!isLast) {
        await TimeUtil.sleep(delayInMs)
      }
    }

    // Use reduce to chain promises sequentially
    await slideList.reduce(async (previousPromise, slide, index) => {
      await previousPromise
      const slideId = slide.id || slide // Handle both object and primitive cases
      const isLast = index === slideList.length - 1
      return fetchSlideWithDelay(slideId, isLast)
    }, Promise.resolve())
  },

  prefetchNextSlideAction: async ({ state, dispatch, commit }) => {
    const currentIndex = state.slideList.findIndex((slide) => slide.id === state.activeSlideId)
    if (currentIndex >= 0 && currentIndex < state.slideList.length - 1) {
      const nextSlide = state.slideList[currentIndex + 1]
      // Don't prefetch leaderboard slides to prevent loading outdated data
      if (nextSlide.type !== 'leaderboard') {
        commit('clearSlideMap')
        await dispatch('getSlideDataAction', nextSlide.id)
      }
    }
  },

  fetchSlideAction: async (_, slideId) => {
    if (!slideId) return null
    const slide = await SlideApi.getSlideDetailV2(slideId, true)
    if (!slide.id) return null
    if (slide.canvasBlocksUrl) {
      const response = await fetch(slide.canvasBlocksUrl)
      const body = await response.json()
      slide.canvasBlocks = body
    }
    return SlideUtil.formatSlide(slide)
  },

  fetchSlideDataAndAttributesAction: async ({ dispatch }, slideId) => {
    if (!slideId) return null
    const [slide, slideAttributes] = await Promise.all([
      dispatch('fetchSlideAction', slideId),
      dispatch('getSlideAttributesBySlideAction', slideId, { root: true }),
    ])
    return {
      ...slide,
      attributes: slideAttributes,
    }
  },

  getSlideDataAction: async ({
    state, commit, dispatch, getters,
  }, slideId) => {
    if (slideId in state.slideMap) {
      return JSON.parse(JSON.stringify(state.slideMap[slideId]))
    }
    let pendingReq = onGoingRequestMap[slideId]
    try {
      pendingReq = pendingReq || dispatch(getters.fetchSlideActionName, slideId)
      onGoingRequestMap[slideId] = pendingReq
      const slide = await pendingReq
      commit('updateSlideMap', slide)
      return JSON.parse(JSON.stringify(slide))
    } finally {
      delete onGoingRequestMap[slideId]
    }
  },

  syncActiveSlideAction: async ({ dispatch }, sessionId) => {
    const key = `/activeSlide/${sessionId}`
    dispatch('kvSync/subscribeKeyAction', {
      key,
      isNotFetch: true,
    }, { root: true })
    let value = await dispatch('kvSync/fetchValueByKeyWithoutUpdateAction', {
      key,
    }, {
      root: true,
    })
    if (value) {
      value = JSON.parse(value)
      const doc = {
        slideActive: value.id,
        quizTimestamp: value.quizTimestamp,
        quizStatus: value.quizStatus,
      }
      if (doc.quizTimestamp) {
        socket.handleSocketQuizTimestamp(doc)
      }
      socket.handleConfirmNextSlide(doc)
    }
  },

}

const mutations = {
  updateEditor: (state, editor) => {
    state.editor = editor
  },

  updateSlideMap: (state, payload) => {
    // WHY: leaderboard will be calculated later, so it will be outdated. So always fetch the latest leaderboard data to get correct leaderboard result of participants.
    // Use case: presenter play quiz 2 first, then navigate to leaderboard, then presenter navigate back to quiz 1, then play quiz 1, then navigate again to leaderboard
    if (payload?.type == 'leaderboard') {
      return
    }
    if (!payload?.id) {
      return
    }
    if (!state.slideMap[payload.id]) {
      state.slideMap[payload.id] = {}
    }
    state.slideMap[payload.id] = { ...state.slideMap[payload.id], ...payload }
  },

  clearSlideMap: (state) => {
    state.slideMap = {}
  },

  setSlideList: (state, slideList) => {
    state.slideList = slideList
  },

  setActiveSlideId: (state, slideId) => {
    state.activeSlideId = slideId
  },

  setPresenting: (state, status) => {
    state.isPresenting = status
  },
  setTimestamp: (state, timestamp) => {
    state.timestamp = timestamp
  },
}

const mutationSubscribers = {
  setSlides: ({ store, mutation }) => {
    store.commit('nextback/setSlideList', mutation.payload)
    store.dispatch('nextback/prefetchNextSlideAction', mutation.payload)
    const presentationId = store.state.presentation.id
    if (!presentationId) {
      throw Error('PresentationId is null or undefined, fetching sequene might be wrong')
    }
  },
  setPresentation: ({ store, mutation }) => {
    store.commit('nextback/setActiveSlideId', mutation.payload.activeSlide)
    const presentationId = mutation.payload.id
    store.dispatch('nextback/syncActiveSlideAction', presentationId)
    store.commit(NEXTBACK_SET_PRESENTING, mutation.payload.presenting)
    store.dispatch('nextback/prefetchNextSlideAction')
  },
}

const actionSubscribers = {
  'kvSync/updateKeyValueAction': ({ action, store }) => {
    const { key, value, packet } = action.payload

    if (key.includes('/activeSlide/') && value) {
      const message = JSON.parse(value)
      store.dispatch('nextback/changeSlideAction', message)
      if (!packet?.retain) {
        // log delay when message is not retained (from mqtt)
        logFn('changeSlideAction: delay', null, { nextbackDelayMs: new Date().getTime() - message.timestamp })
      }
    }
  },
  'websocket/newMessage': async ({ action, store }) => {
    /**
     * Since we ignore next slide command when not presenting, audience will not follow presenter
     * when editing in editor. When they click `present`, active slide
     * must be synced again
     */
    const update = action.payload
    if (update.type === 'presentation' && update.action === 'presentation-update') {
      if (update.data.presenting === 'false') {
        store.commit('nextback/clearSlideMap')
        store.commit(NEXTBACK_SET_PRESENTING, false)
      }
    }
    if (update.type === 'presentation' && update.action === 'presenting') {
      // Audience can join the presentation while Presenter still on editing mode. So when presenter hit Present, we need to clear out everything and refetch the latest slide data for the Presenting mode
      store.commit('nextback/clearSlideMap')
      const slide = await store.dispatch('nextback/getSlideDataAction', store.state?.nextback?.activeSlideId)
      store.dispatch('updateSlide', slide)
      store.commit(NEXTBACK_SET_PRESENTING, true)
    }
  },
}

const plugin =
  (store) => {
    store.subscribe((mutation) => {
      if (!store.getters['featureToggles/canUseNewChangeSlideMechanism']) {
        return
      }
      if (mutation.type in mutationSubscribers) {
        mutationSubscribers[mutation.type]({ store, mutation })
      }
    })

    store.subscribeAction((action) => {
      if (!store.getters['featureToggles/canUseNewChangeSlideMechanism']) {
        return
      }
      if (action.type in actionSubscribers) {
        actionSubscribers[action.type]({ store, action })
      }
    })
  }

export default {
  namespaced: true,
  state: _state,
  actions,
  mutations,
  plugin,
  getters: getterMethods,
}
